import { useContext, useEffect, useRef, useState } from 'react'

import { createLatestRequestController } from '../../../api/latest-request'
import { getStorageInfo, getVarWwwDirectories, getYaskillVarWwwDirectories } from '@api/systems/api'
import { IDirectoryUsage, IStorageInfo } from '@api/systems/api.types'
import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'
import { reportUnexpectedClientError } from '@services/client-error.service'

import { SystemsUI } from './ui'
import { TDirectorySource } from './types'


type DirectoryState = Record<TDirectorySource, {
    items: IDirectoryUsage[]
    isLoading: boolean
}>

const DIRECTORY_POLL_INTERVAL_MS = 1000

const waitForDirectoryPoll = (signal: AbortSignal) => new Promise<void>((resolve) => {
    if (signal.aborted) {
        resolve()
        return
    }

    let timeoutID: ReturnType<typeof setTimeout>
    const finish = () => {
        clearTimeout(timeoutID)
        signal.removeEventListener('abort', finish)
        resolve()
    }
    timeoutID = setTimeout(finish, DIRECTORY_POLL_INTERVAL_MS)
    signal.addEventListener('abort', finish, {once: true})
})


/** Контейнер UI - Система */
export const SystemContainer = () => {
    const notify = useContext(NotificationContext)
    const storageRequest = useRef(createLatestRequestController()).current
    const directoryRequests = useRef({
        crm: createLatestRequestController(),
        yaskill: createLatestRequestController(),
    }).current

    const [isLoadingPage, setIsLoadingPage] = useState(true)
    const [isAccessDenied, setIsAccessDenied] = useState(false)
    const [storageInfo, setStorageInfo] = useState<IStorageInfo | null>(null)

    const [directorySource, setDirectorySource] = useState<TDirectorySource | null>(null)
    const [directoryState, setDirectoryState] = useState<DirectoryState>({
        crm: {items: [], isLoading: false},
        yaskill: {items: [], isLoading: false},
    })

    const loadStorageInfo = async () => {
        const request = storageRequest.start()
        setIsLoadingPage(true)

        try {
            const response = await getStorageInfo(request.signal)
            if (!request.isCurrent()) return

            if (response.status === 200 && response.data) {
                setStorageInfo(response.data)
                setIsAccessDenied(false)
                return
            }

            if (response.status === 403) {
                setStorageInfo(null)
                setIsAccessDenied(true)
                return
            }

            if (response.status !== 499) {
                notify && notifyOpen(response.message, 'error', 2500, notify)
            }
        } catch (error) {
            if (!request.isCurrent()) return
            reportUnexpectedClientError(error, 'Systems.getStorageInfo')
            notify && notifyOpen('Не удалось получить информацию о дисках.', 'error', 2500, notify)
        } finally {
            if (request.isCurrent()) setIsLoadingPage(false)
            request.finish()
        }
    }

    const loadDirectories = async (source: TDirectorySource) => {
        const requestController = directoryRequests[source]
        const request = requestController.start()
        const getDirectories = source === 'crm' ? getVarWwwDirectories : getYaskillVarWwwDirectories
        const serverName = source === 'crm' ? 'CRM' : 'YASKILL'
        setDirectoryState((current) => ({
            ...current,
            [source]: {...current[source], isLoading: true},
        }))
        let shouldRefresh = true

        try {
            while (request.isCurrent()) {
                const response = await getDirectories(shouldRefresh, request.signal)
                if (!request.isCurrent()) return

                if (response.status === 200 && response.data) {
                    setDirectoryState((current) => ({
                        ...current,
                        [source]: {...current[source], items: response.data?.items || []},
                    }))
                    if (!response.data.isRefreshing) return

                    shouldRefresh = false
                    await waitForDirectoryPoll(request.signal)
                    continue
                }

                if (response.status === 403) {
                    setDirectoryState((current) => ({
                        ...current,
                        [source]: {items: [], isLoading: false},
                    }))
                    setIsAccessDenied(true)
                    setDirectorySource(null)
                    return
                }

                if (response.status !== 499) {
                    notify && notifyOpen(response.message, 'error', 2500, notify)
                }
                return
            }
        } catch (error) {
            if (!request.isCurrent()) return
            reportUnexpectedClientError(error, `Systems.get${serverName}VarWwwDirectories`)
            notify && notifyOpen(`Не удалось получить размеры каталогов ${serverName}.`, 'error', 2500, notify)
        } finally {
            if (request.isCurrent()) {
                setDirectoryState((current) => ({
                    ...current,
                    [source]: {...current[source], isLoading: false},
                }))
            }
            request.finish()
        }
    }

    useEffect(() => {
        void loadStorageInfo()

        return () => {
            storageRequest.invalidate()
            directoryRequests.crm.invalidate()
            directoryRequests.yaskill.invalidate()
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const handleRefresh = () => {
        void loadStorageInfo()
        void loadDirectories('crm')
        void loadDirectories('yaskill')
    }

    const handleOpenDirectoryModal = (source: TDirectorySource) => {
        setDirectoryState((current) => ({
            ...current,
            [source]: {items: [], isLoading: false},
        }))
        setDirectorySource(source)
        void loadDirectories(source)
    }

    const handleCloseDirectoryModal = () => {
        if (directorySource) {
            directoryRequests[directorySource].invalidate()
            setDirectoryState((current) => ({
                ...current,
                [directorySource]: {items: [], isLoading: false},
            }))
        }
        setDirectorySource(null)
    }

    const activeDirectoryState = directorySource ? directoryState[directorySource] : null

    return <SystemsUI
        isLoadingPage={isLoadingPage}
        isAccessDenied={isAccessDenied}
        storageInfo={storageInfo}
        isDirectoryModalOpen={directorySource !== null}
        directorySource={directorySource}
        isLoadingDirectories={activeDirectoryState?.isLoading || false}
        directoryItems={activeDirectoryState?.items || []}
        handleRefresh={handleRefresh}
        handleOpenDirectoryModal={handleOpenDirectoryModal}
        handleCloseDirectoryModal={handleCloseDirectoryModal}
    />
}